import { apiFetch, readErrorMessage, readJson } from './client';

export interface EmojiCatalogItem {
  id: string;
  externalId: string;
  emoji: string;
  name: string;
  category: string;
  keywords: string[];
  isActive: boolean;
  origin: string;
  sortOrder: number;
}

export interface EmojiCatalogPage {
  items: EmojiCatalogItem[];
  total: number;
  skip: number;
  take: number;
}

export interface EmojiCatalogCategory {
  name: string;
  count: number;
}

export interface EmojiCatalogQuery {
  q?: string;
  category?: string;
  skip?: number;
  take?: number;
}

export interface EmojiCatalogUpsertRequest {
  emoji: string;
  name: string;
  category: string;
  keywords?: string[];
  isActive: boolean;
}

function toQueryString(query: EmojiCatalogQuery): string {
  const parts: string[] = [];
  if (query.q) parts.push(`q=${encodeURIComponent(query.q)}`);
  if (query.category) parts.push(`category=${encodeURIComponent(query.category)}`);
  if (query.skip != null) parts.push(`skip=${encodeURIComponent(String(query.skip))}`);
  if (query.take != null) parts.push(`take=${encodeURIComponent(String(query.take))}`);
  return parts.length === 0 ? '' : `?${parts.join('&')}`;
}

function asPage(raw: Partial<EmojiCatalogPage>): EmojiCatalogPage {
  return {
    items: raw.items ?? [],
    total: Number(raw.total ?? 0),
    skip: Number(raw.skip ?? 0),
    take: Number(raw.take ?? 0)
  };
}

export async function listEmojiCatalog(query: EmojiCatalogQuery = {}): Promise<EmojiCatalogPage> {
  const res = await apiFetch(`/api/emoji-catalog${toQueryString(query)}`);
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return asPage(await readJson<Partial<EmojiCatalogPage>>(res));
}

export async function listEmojiCatalogCategories(): Promise<EmojiCatalogCategory[]> {
  const res = await apiFetch('/api/emoji-catalog/categories');
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<EmojiCatalogCategory[]>(res);
}

export async function listEmojiCatalogAdmin(query: EmojiCatalogQuery = {}): Promise<EmojiCatalogPage> {
  const res = await apiFetch(`/api/emoji-catalog/admin/items${toQueryString(query)}`);
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return asPage(await readJson<Partial<EmojiCatalogPage>>(res));
}

export async function listEmojiCatalogAdminCategories(): Promise<EmojiCatalogCategory[]> {
  const res = await apiFetch('/api/emoji-catalog/admin/categories');
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<EmojiCatalogCategory[]>(res);
}

export async function createEmojiCatalogItem(
  body: EmojiCatalogUpsertRequest
): Promise<EmojiCatalogItem> {
  const res = await apiFetch('/api/emoji-catalog/admin/items', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<EmojiCatalogItem>(res);
}

export async function updateEmojiCatalogItem(
  id: string,
  body: EmojiCatalogUpsertRequest
): Promise<EmojiCatalogItem> {
  const res = await apiFetch(`/api/emoji-catalog/admin/items/${encodeURIComponent(id)}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body)
  });
  if (!res.ok) throw new Error(await readErrorMessage(res));
  return readJson<EmojiCatalogItem>(res);
}

export async function deactivateEmojiCatalogItem(id: string): Promise<void> {
  const res = await apiFetch(`/api/emoji-catalog/admin/items/${encodeURIComponent(id)}/deactivate`, {
    method: 'POST'
  });
  if (!res.ok) throw new Error(await readErrorMessage(res));
}
